import Layer from "../../Layer";
import PageHeader from "../../Component/PageHeader";
import CardSalesScorecardResults from "../../Component/Card/Report/CardSalesScorecardResults";
import reportsService from "../../Seating/Security/ReportsService/reportsService";
import moment from "moment";
import CardReportSettings from "../../Component/Card/Report/CardReportSettings";
import Setting from "../../Utility/Setting";

export default class SalesScorecard extends Layer {
    constructor(props) {

        super(props);
        this.rows = [];
        this.columns = [];
        this.defaultColumnWidths = [];
        this.exportFilename = null;
        this.loadedAt = null;
        this.loading = false;
    }

    renderHeader() {
        return <PageHeader title={this.getTitle()} />;
    }

    getTitle() {
        return "Sales Scorecard";
    }

    getMaxWidth() {
        return 1600;
    }

    afterComponentDidMount() {
        const settingKey = window.location.pathname
            .replace(/^\/|\/$/g, "")
            .replace(/\//g, ".") + ".settings";

        const savedSettings = Setting.get(settingKey);

        if (savedSettings?.date?.date) {
            this.runReport(savedSettings);
        } else {
            this.runReport({
                year: {
                    date: moment().startOf("year").toISOString(),
                },
            });
        }
    }

    /**
     * Extracts the locations tierPoints from the jsonTierPoints data.
     * @param {Object} location current location
     * @returns {Array} array of TierPoints.
     */
    normalizeTierRanges(location) {
        if (!location?.jsonTierPoints) {
            return [];
        }

        if (Array.isArray(location.jsonTierPoints)) {
            return location.jsonTierPoints;
        }

        try {
            return JSON.parse(location.jsonTierPoints);
        } catch {
            return [];
        }
    }

    /**
     * Returns the tier range effective at the earlier of today or the end of
     * the selected report year.
     *
     * @param {Object} location current location
     * @param {Number} reportYear selected report year
     * @returns {Object|undefined} effective tier range
     */
    getEffectiveTierRange(location, reportYear) {
        const today = moment().startOf("day");
        const reportYearEnd = moment(`${reportYear}-12-31`).startOf("day");
        const effectiveDate = moment.min(today, reportYearEnd);

        return this.normalizeTierRanges(location).find((range) => {
            const begin = moment(range.begin, "YYYY-MM-DD", true);
            const end = range.end ? moment(range.end, "YYYY-MM-DD", true) : null;

            return effectiveDate.isSameOrAfter(begin)
                && (!end || effectiveDate.isSameOrBefore(end));
        });
    }

    /**
     * Builds the current tier and tooltip for changes within the year.
     * @param {*} location current location
     * @param {*} year selected year
     * @returns {Set, Set, Set, String} tier values and tooltip.
     */
    buildTierColumnModel(location, year) {
        const ranges = this.getTierRangesForYear(location, year);

        if (!ranges.length) {
            return null;
        }

        const tier1 = [];
        const tier2 = [];
        const tier3 = [];
        const tooltipLines = [];

        for (const range of ranges) {
            tier1.push(range.tier1Points);
            tier2.push(range.tier2Points);
            tier3.push(range.tier3Points);

            const begin = moment(range.begin, "YYYY-MM-DD", true).format("MMM D, YYYY");
            const end = range.end
                ? moment(range.end, "YYYY-MM-DD", true).format("MMM D, YYYY")
                : "Present";

            tooltipLines.push(
                `${begin} – ${end}: ${range.tier1Points} / ${range.tier2Points} / ${range.tier3Points}`
            );
        }

        return {
            tier1Values: [...new Set(tier1)],
            tier2Values: [...new Set(tier2)],
            tier3Values: [...new Set(tier3)],
            tooltip: tooltipLines.join("\n")
        };
    }

    /**
     * Builds the tooltip which states if any tier changes have occured within the year.
     * @param {*} monthlyTiers What the tiers look like month to month Jan-Dec
     * @returns {String} tooltip
     */
    buildCollapsedTierTooltip(monthlyTiers) {
        const entries = Object.entries(monthlyTiers)
            .sort(([a], [b]) => a.localeCompare(b));

        const ranges = [];
        let current = null;

        for (const [yearMonth, tier] of entries) {
            const key = `${tier.tier1Points}|${tier.tier2Points}|${tier.tier3Points}`;

            if (!current) {
                current = {
                    start: yearMonth,
                    end: yearMonth,
                    key,
                    tier
                };
                continue;
            }

            const prevMonth = moment(`${current.end}-01`).add(1, "month").format("YYYY-MM");

            if (key === current.key && yearMonth === prevMonth) {
                current.end = yearMonth;
            } else {
                ranges.push(current);
                current = {
                    start: yearMonth,
                    end: yearMonth,
                    key,
                    tier
                };
            }
        }

        if (current) {
            ranges.push(current);
        }

        return ranges.map(range => {
            const startLabel = moment(`${range.start}-01`).format("MMM YYYY");
            const endLabel = moment(`${range.end}-01`).format("MMM YYYY");

            const dateLabel =
                range.start === range.end
                    ? startLabel
                    : `${startLabel} – ${endLabel}`;

            return `${dateLabel}: ${range.tier.tier1Points} / ${range.tier.tier2Points} / ${range.tier.tier3Points}`;
        }).join("\n");
    }

    /**
     * Builds the tier ranges for a given year
     * @param {*} location current location
     * @param {*} year current or selected year
     * @returns
     */
    getTierRangesForYear(location, year) {
        const ranges = this.normalizeTierRanges(location);
        const yearStart = moment(`${year}-01-01`);
        const yearEnd = moment(`${year}-12-31`);

        return ranges.filter(range => {
            const begin = moment(range.begin, "YYYY-MM-DD", true);
            const end = range.end ? moment(range.end, "YYYY-MM-DD", true) : null;

            return begin.isSameOrBefore(yearEnd)
                && (!end || end.isSameOrAfter(yearStart));
        });
    }

    /**
     * Builds the monthly tier array keyed by year-month
     * that contains the begin, end, and tier values of that month.
     * @param {*} location current location
     * @param {*} yearMonthKeys xxxx-01...xxxx-12
     * @returns {Array} monthlyTiers
     */
    buildMonthlyTiers(location, yearMonthKeys) {
        const ranges = this.normalizeTierRanges(location);
        const monthlyTiers = {};

        for (const yearMonth of yearMonthKeys) {
            const monthDate = moment(`${yearMonth}-01`);

            const matchingRange = ranges.find((range) => {
                const begin = moment(range.begin, "YYYY-MM-DD", true);
                const end = range.end ? moment(range.end, "YYYY-MM-DD", true) : null;

                return monthDate.isSameOrAfter(begin)
                    && (!end || monthDate.isSameOrBefore(end));
            });

            if (matchingRange) {
                monthlyTiers[yearMonth] = {
                    tier1Points: matchingRange.tier1Points,
                    tier2Points: matchingRange.tier2Points,
                    tier3Points: matchingRange.tier3Points
                };
            }
        }

        return monthlyTiers;
    }


    /**
   * Loads the Sales Scorecard report data from the backend.
   * Groups delivered/approved items by region/location, builds columns,
   * and prepares the rows for rendering.
   *
   * @param {object} settings - Optional report settings containing dateRange.
   * @param {string|null} settings.dateRange.beginDate - Start date filter.
   * @param {string|null} settings.dateRange.endDate - End date filter.
   */
    async runReport(settings) {
        this.loading = true;
        this.forceUpdate();

        try {
            const selectedYear = settings?.date?.date
                ? moment(settings.date.date).year()
                : moment().year();

            this.selectedYear = selectedYear;

            const beginDate = `${selectedYear}-01-01`;
            const res = await reportsService.getSalesScorecardReport({ beginDate });

            const payload = res?.data || res;
            const delivered = payload?.data?.delivered || payload?.delivered || [];
            const approved = payload?.data?.approved || payload?.approved || [];

            const groupedDelivered = this.groupByRegionId(delivered);
            const groupedApproved = this.groupByRegionId(approved);
            const yearMonthKeys = this.getYearMonthKeys(delivered);

            this.columns = this.buildColumns(yearMonthKeys);
            this.defaultColumnWidths = this.buildColumnWidths(this.columns);

            const { locationRows, regionTotals } =
                this.buildRowsFromGroupedData(
                    groupedDelivered,
                    groupedApproved,
                    yearMonthKeys,
                    selectedYear
                );

            this.rows = this.assembleFinalRows(
                locationRows,
                regionTotals,
                this.buildSpecialRows(locationRows, yearMonthKeys)
            );

            this.loadedAt = new Date();
            this.exportFilename =
                `${this.getTitle()} ${selectedYear} - ${moment(this.loadedAt).format("YYYY-MM-DD h mm a")}.csv`;
        } catch (error) {
            console.error("SalesScorecard Report failed", error);
        } finally {
            this.loading = false;
            this.forceUpdate();
        }
    }


    /**
  * Builds the column definitions for the scorecard table.
  * Includes static columns plus one for each delivered year-month key.
  *
  * @param {string[]} yearMonthKeys - Array of YYYY-MM strings.
  * @returns {Array<object>} Array of column definitions.
  */
    buildColumns(yearMonthKeys) {
        const selectedYear = this.selectedYear ?? moment().year();

        const currentMonthKey =
            selectedYear === moment().year()
                ? moment().format("YYYY-MM")
                : `${selectedYear}-12`;
        const includedMonthKeys = yearMonthKeys.filter(
            (yearMonthKey) => yearMonthKey <= currentMonthKey
        );


        return [
            { title: "Region / Location", name: "regionLocation", type: "string", width: 135 },
            { title: "Tier 1", name: "tier1Points", type: "number", width: 60, align: "right" },
            { title: "Tier 2", name: "tier2Points", type: "number", width: 60, align: "right" },
            { title: "Tier 3", name: "tier3Points", type: "number", width: 60, align: "right" },
            ...includedMonthKeys.map((yearMonth, index) => {
                const [year, month] = yearMonth.split("-");
                return {
                    title: moment(`${year}-${month}-01`).format("MMM"),
                    name: yearMonth,
                    type: "number",
                    width: 80,
                    align: "right",
                    subType: "monthValue",
                    isCurrentMonth:
                        selectedYear === moment().year() &&
                        index === includedMonthKeys.length - 1
                };
            }),
            { title: `Approved`, name: "approved_total", type: "number", width: 80, align: "right" },
            { title: "YTD Total", name: "ytd_total", type: "number", width: 80, align: "right" },
            { title: "Year Goal", name: "ytd_goal", type: "number", width: 80, align: "right" },
            { title: "YTD Gap", name: "ytd_gap", type: "number", width: 80, align: "right" },
            { title: "AVG Needed", name: "avg_needed", type: "number", width: 100, align: "right" },
        ];
    }

    buildColumnWidths(columns) {
        return columns.map(col => ({ columnName: col.name, width: col.width || 160 }));
    }
    /**
      * Groups a list of data items by their region id.
      * Skips locations not in the current user's regions.
      *
      * @param {Array<object>} dataList - Delivered/approved data items.
      * @returns {object} Object with { regions: { [regionId]: items[] } }.
      */
    groupByRegionId(dataList) {
        const result = {};
        for (const item of dataList) {
            const locationId = item.location_id;
            const location = this.context?.locationsIndexed?.[locationId];
            const regionId = location?.regionId ?? null;
            if (!regionId) {
                continue;
            }
            if (!result[regionId]) {
                result[regionId] = [];
            }
            result[regionId].push(item);
        }
        return { regions: result };
    }

    /**
   * Groups a list of items by their location_id field.
   *
   * @param {Array<object>} list - Data items.
   * @returns {object} Map of { [locationId]: items[] }.
   */
    groupByLocationId(list) {
        return list.reduce((acc, item) => {
            const id = item.location_id;
            if (!acc[id]) { acc[id] = []; }
            acc[id].push(item);
            return acc;
        }, {});
    }

    /**
  * Extracts unique year-month keys from delivered data.
  *
  * @param {Array<object>} deliveredList - Delivered items with year/month fields.
  * @returns {string[]} Sorted array of YYYY-MM keys.
  */
    getYearMonthKeys(deliveredList) {
        const yearMonthSet = new Set();
        for (const item of deliveredList) {
            if (item?.year && item?.month) {
                yearMonthSet.add(`${item.year}-${String(item.month).padStart(2, "0")}`);
            }
        }
        return Array.from(yearMonthSet).sort();
    }

    /**
    * Calculates YTD totals, goals, gap, and average needed for a row.
    *
    * @param {object} row - Data row being mutated.
    * @param {string[]} yearMonthKeys - List of months included in the report.
    */
    calculateTotals(row, yearMonthKeys) {
        const selectedYear = this.selectedYear ?? moment().year();

        const currentMonthKey =
            selectedYear === moment().year()
                ? moment().format("YYYY-MM")
                : `${selectedYear}-12`;

        const completedMonths = yearMonthKeys.filter(
            (yearMonth) => yearMonth < currentMonthKey
        );

        const remainingMonths = yearMonthKeys.filter(
            (yearMonth) => yearMonth >= currentMonthKey
        );

        // 1. YTD delivered (completed months only)
        const ytdDelivered = completedMonths.reduce(
            (sum, yearMonth) => sum + (row[yearMonth] ?? 0),
            0
        );

        row.ytd_total = +ytdDelivered.toFixed(2);

        // Rows without monthly tiers cannot compute goals
        if (!row.monthlyTiers) {
            row.ytd_goal = "";
            row.ytd_gap = "";
            row.avg_needed = "";
            return;
        }

        // 2. Year Goal = Tier 3 sum for all months
        const yearGoal = yearMonthKeys.reduce((sum, yearMonth) => {
            return sum + (row.monthlyTiers[yearMonth]?.tier3Points ?? 0);
        }, 0);

        row.ytd_goal = +yearGoal.toFixed(2);
        row.ytd_gap = +(row.ytd_total - row.ytd_goal).toFixed(2);

        // 3. AVG Needed = pace required to still hit Year Goal
        row.avg_needed =
            remainingMonths.length > 0
                ? +((yearGoal - row.ytd_total) / remainingMonths.length).toFixed(2)
                : 0;
    }

    /**
   * Builds location-level and region-total rows from grouped data.
   *
   * @param {object} groupedDelivered - Delivered items grouped by region.
   * @param {object} groupedApproved - Approved items grouped by region.
   * @param {string[]} yearMonthKeys - Months to include.
    * @param {number} reportYear - Selected report year.
    * @returns {object} { locationRows, regionTotals }.
    */
    buildRowsFromGroupedData(groupedDelivered, groupedApproved, yearMonthKeys, reportYear) {
        const locationRows = [];
        const regionTotals = {};
        for (const [regionId, deliveredItems] of Object.entries(groupedDelivered.regions)) {
            const approvedItems = groupedApproved.regions?.[regionId] || [];
            const regionName = this.context?.regionsIndexed?.[regionId]?.name || "Unknown Region";

            const regionTotal = {
                id: `total-${regionName}`,
                rowType: "region-total",
                region: regionName,
                location: "Region Total",
                tier1Points: 0,
                tier2Points: 0,
                tier3Points: 0,
                approved_total: 0,
                ytd_total: 0,
                ytd_goal: 0,
                ytd_gap: 0,
                avg_needed: "",
                ...Object.fromEntries(yearMonthKeys.map(yearMonth => [yearMonth, 0])),
            };

            const deliveredByLocation = this.groupByLocationId(deliveredItems);
            const approvedByLocation = this.groupByLocationId(approvedItems);

            for (const locationId of Object.keys(deliveredByLocation)) {
                const location = this.context.locationsIndexed?.[locationId];
                if (!location) {
                    continue;
                }

                const tierRangesForYear = this.getTierRangesForYear(location, reportYear);
                if (!tierRangesForYear.length) {
                    continue;
                }

                const monthlyTiers = this.buildMonthlyTiers(location, yearMonthKeys);

                // Monthly tiers preserve the selected year's history for goals and the tooltip.
                const tier1Set = new Set();
                const tier2Set = new Set();
                const tier3Set = new Set();

                Object.values(monthlyTiers).forEach((tier) => {
                    tier1Set.add(tier.tier1Points);
                    tier2Set.add(tier.tier2Points);
                    tier3Set.add(tier.tier3Points);
                });
                const tier1Values = Array.from(tier1Set);
                const tier2Values = Array.from(tier2Set);
                const tier3Values = Array.from(tier3Set);
                const hasMultipleTiers =
                    tier1Values.length > 1 ||
                    tier2Values.length > 1 ||
                    tier3Values.length > 1;
                const tierSuffix = hasMultipleTiers ? "*" : "";

                // Past reports use the tier effective at year-end. Current and future
                // reports use today's tier so future-dated ranges remain hidden.
                const effectiveTier = this.getEffectiveTierRange(location, reportYear);
                const effectiveTier1Points = effectiveTier?.tier1Points;
                const effectiveTier2Points = effectiveTier?.tier2Points;
                const effectiveTier3Points = effectiveTier?.tier3Points;
                const formatEffectiveTier = (value) => value === undefined
                    ? ""
                    : value + tierSuffix;

                const baseTooltip = this.buildCollapsedTierTooltip(monthlyTiers);
                const collapsedTooltip = hasMultipleTiers
                    ? `* Tier values changed during the year\n\n${baseTooltip}`
                    : baseTooltip;

                const row = {
                    id: Number(locationId),
                    rowType: "location",
                    region: regionName,
                    location: location.name || `#${locationId}`,

                    tier1Points: formatEffectiveTier(effectiveTier1Points),
                    tier2Points: formatEffectiveTier(effectiveTier2Points),
                    tier3Points: formatEffectiveTier(effectiveTier3Points),

                    monthlyTiers: monthlyTiers,

                    tierTooltip: collapsedTooltip,

                    approved_total: approvedByLocation[locationId]?.[0]?.sum_points || 0,
                };

                for (const yearMonth of yearMonthKeys) {
                    row[yearMonth] =
                        deliveredByLocation[locationId]?.find(
                            i => `${i.year}-${String(i.month).padStart(2, "0")}` === yearMonth
                        )?.sum_points || 0;
                    regionTotal[yearMonth] += row[yearMonth];
                }

                regionTotal.tier1Points += effectiveTier1Points ?? 0;
                regionTotal.tier2Points += effectiveTier2Points ?? 0;
                regionTotal.tier3Points += effectiveTier3Points ?? 0;
                regionTotal.approved_total += row.approved_total;

                this.calculateTotals(row, yearMonthKeys);
                regionTotal.ytd_total += row.ytd_total ?? 0;
                regionTotal.ytd_goal += row.ytd_goal ?? 0;
                locationRows.push(row);
            }
            regionTotal.ytd_total = +regionTotal.ytd_total.toFixed(2);
            regionTotal.ytd_goal = +regionTotal.ytd_goal.toFixed(2);
            regionTotal.ytd_gap = +(regionTotal.ytd_total - regionTotal.ytd_goal).toFixed(2);

            const selectedYear = this.selectedYear ?? moment().year();

            const currentMonthKey =
                selectedYear === moment().year()
                    ? moment().format("YYYY-MM")
                    : `${selectedYear}-12`;
            const remainingMonthsCount = yearMonthKeys.filter(
                (yearMonth) => yearMonth >= currentMonthKey
            ).length;

            regionTotal.avg_needed =
                remainingMonthsCount > 0
                    ? +((regionTotal.ytd_goal - regionTotal.ytd_total) / remainingMonthsCount).toFixed(2)
                    : 0;

            regionTotals[regionName] = regionTotal;
        }
        return { locationRows, regionTotals };
    }

    /**
 * Assembles the final ordered rows for the table.
 * Includes region totals, location rows, and special rows.
 *
 * @param {Array<object>} locationRows - Rows for each location.
 * @param {object} regionTotals - Totals by region.
 * @param {Array<object>} specialRows - Grand total/month/YTD rows.
 * @returns {Array<object>} Array of all rows in display order.
 */
    assembleFinalRows(locationRows, regionTotals, specialRows) {
        const rows = [];
        const grouped = locationRows.reduce((acc, row) => {
            acc[row.region] = acc[row.region] || [];
            acc[row.region].push(row);
            return acc;
        }, {});

        for (const regionName of Object.keys(regionTotals).sort()) {
            rows.push(regionTotals[regionName], ...(grouped[regionName] || []).sort((a, b) => a.location.localeCompare(b.location)));
        }

        rows.push(...specialRows);
        return rows;
    }

    /**
     * Builds the grand total, monthly, and YTD special rows.
     *
     * @param {Array<object>} locationRows - All location rows.
     * @param {string[]} yearMonthKeys - Months to include.
     * @returns {Array<object>} [totalsRow, monthRow, ytdRow].
     */
    buildSpecialRows(locationRows, yearMonthKeys) {
        const selectedYear = this.selectedYear ?? moment().year();

        const tier1Total = locationRows.reduce((s, r) => s + (parseFloat(r.tier1Points) || 0), 0);
        const tier2Total = locationRows.reduce((s, r) => s + (parseFloat(r.tier2Points) || 0), 0);
        const tier3Total = locationRows.reduce((s, r) => s + (parseFloat(r.tier3Points) || 0), 0);

        const currentMonthKey =
            selectedYear === moment().year()
                ? moment().format("YYYY-MM")
                : `${selectedYear}-12`;

        const remainingMonthsCount = yearMonthKeys.filter(
            (yearMonth) => yearMonth >= currentMonthKey
        ).length;

        // Monthly totals across all locations
        const monthTotals = Object.fromEntries(
            yearMonthKeys.map(yearMonth => [
                yearMonth,
                locationRows.reduce(
                    (sum, row) => sum + (row[yearMonth] ?? 0),
                    0
                )
            ])
        );

        // YTD Total = sum of location YTDs
        const ytdTotal = locationRows.reduce(
            (sum, row) => sum + (row.ytd_total ?? 0),
            0
        );

        // Year Goal = sum of location year goals
        const yearGoal = locationRows.reduce(
            (sum, row) => sum + (row.ytd_goal ?? 0),
            0
        );

        const totalsRow = {
            id: "grand-totals",
            rowType: "totals",
            region: "",
            location: "Totals",
            tier1Points: +tier1Total.toFixed(2),
            tier2Points: +tier2Total.toFixed(2),
            tier3Points: +tier3Total.toFixed(2),
            approved_total: locationRows.reduce(
                (sum, row) => sum + (row.approved_total ?? 0),
                0
            ),
            ...monthTotals,
            ytd_total: +ytdTotal.toFixed(2),
            ytd_goal: +yearGoal.toFixed(2),
            ytd_gap: +(ytdTotal - yearGoal).toFixed(2),
            avg_needed:
                remainingMonthsCount > 0
                    ? +((yearGoal - ytdTotal) / remainingMonthsCount).toFixed(2)
                    : 0
        };

        return [totalsRow];
    }


    /**
       * Renders the results card with all scorecard data.
       * @returns {JSX.Element} CardSalesScorecardResults component.
       */
    renderContent() {
        return (
            <>
                <CardReportSettings
                    onRun={(settings) => this.runReport(settings)}
                    isLoading={this.loading}
                    initiallyCollapsed={true}
                    settingComponents={{
                        date: {
                            dateViews: ["year"],
                            validationRules: {
                                date: {
                                    required: true,
                                },
                            },
                        },
                    }}
                />

                <CardSalesScorecardResults
                    rows={this.rows}
                    columns={this.columns}
                    defaultColumnWidths={this.defaultColumnWidths}
                    isLoading={this.loading}
                    loadedAt={this.loadedAt}
                    title={this.getTitle()}
                    exportFilename={this.exportFilename}
                />
            </>
        );
    }
}
